/**
 * NFC Solutions — Chime preset library (Session 27.70)
 *
 * A collection of alternative chime patterns managers can pick from
 * so every venue can find a signature that fits their vibe. Each
 * preset defines two role-specific tone-maps:
 *   - `kitchen(role)` — warmer / mid-range
 *   - `bar(role)`     — brighter / higher-pitched
 *
 * A tone-map is just an array of
 *   { freq, t, dur, gain?, type?, hold? }
 * objects — the same shape App.js `_runChimeNow` already consumes.
 * Adding a new preset is 20 lines of arithmetic; no runtime changes
 * needed elsewhere.
 *
 * Storage: the chosen preset ID lives in localStorage per device so
 * a bar can pick "Bright Bell" while the kitchen next door runs
 * "Mellow Marimba" and neither imposes on the other.
 */

const STORAGE_KEY = "nfcs:chime:preset_id";
const DEFAULT_PRESET_ID = "warm_chord";
// Session 27.72 — Per-event preset selection for the Staff dashboard.
// The Staff role hears three distinct sound events during a shift:
//   • new_order    — a fresh order arrived from a customer/QR menu
//   • order_ready  — kitchen or bar marked an order ready to serve
//   • confirmation — the "Confirm before send to kitchen" popup opened
// Each event stores its own preset id so managers can, say, keep the
// order-ready ping cheerful while making new-order chimes louder /
// more attention-grabbing. Falls back to the device-wide default when
// an event has no explicit choice.
const STAFF_EVENT_STORAGE_KEYS = {
  new_order:    "nfcs:chime:staff_event:new_order",
  order_ready:  "nfcs:chime:staff_event:order_ready",
  confirmation: "nfcs:chime:staff_event:confirmation",
  long_wait:    "nfcs:chime:staff_event:long_wait", // Session 30.19
};
const STAFF_EVENT_DEFAULTS = {
  new_order:    "warm_chord",
  order_ready:  "digital_chirp",
  confirmation: "bright_bell",
  long_wait:    "mellow_marimba",
};
const STAFF_EVENT_LABELS = {
  new_order:    "New order",
  order_ready:  "Order ready ping",
  confirmation: "Order confirmation pop-up",
  long_wait:    "Long-wait alert (order over 15 min)",
};

// ─────────────────────────────────────────────────────────────
// Preset library
// ─────────────────────────────────────────────────────────────
//
// Each preset is a plain object with:
//   id           — stable slug (used in localStorage and the picker)
//   label        — human-facing name
//   description  — one-line UI hint
//   kitchen()    — returns the tone array for the kitchen chime
//   bar()        — returns the tone array for the bar chime
//
// Presets all target ~2.0-2.4 seconds total duration so the queue
// timing in App.js still works without special-casing.

const PRESETS = [
  {
    id: "warm_chord",
    label: "Warm Chord",
    description: "Three attention pulses, then a warm major chord for two seconds. Rich, unmistakable, friendly.",
    kitchen: () => {
      // Session 27.91 — Rewritten to eliminate the "distorted /
      // clicking" artifact operators reported on Warm Chord.
      //
      // Root cause: the previous voicing stacked four oscillators
      // during the sustained chord (C4 sine + E4 sine + G4 sine +
      // C5 *triangle*) at gains that summed to ~2.5 raw amplitude,
      // then routed the whole thing through the master compressor
      // (threshold −20 dB, ratio 6:1) + 1.8× post-boost. The
      // compressor's 5 ms attack was too slow to catch the initial
      // transient of four sines locking to phase, so the first
      // ~10 ms of the chord clipped hard against the sample ceiling.
      // The triangle wave on C5 also contributed odd harmonics
      // (1570 Hz, 2620 Hz…) that beat against the sine
      // fundamentals, producing the characteristic "gritty ring".
      //
      // The rewrite:
      //   • All sine — no triangle harmonics on the sustain, so no
      //     inter-harmonic beating.
      //   • Three-note major triad only (C4-E4-G4). Dropped the C5
      //     top voice; the octave doubling was doing nothing musical
      //     that the compressor's harmonic re-generation didn't
      //     already supply.
      //   • Gains reduced so the peak simultaneous amplitude is
      //     around 0.9 raw — safely inside the compressor's linear
      //     region so it lifts loudness without clipping.
      //   • Pulses use a soft sine (no triangle overtone) at 0.55
      //     gain, again well below the compressor threshold.
      const tones = [];
      [0.00, 0.14, 0.28].forEach((t) => {
        tones.push({ freq: 261.63, t, dur: 0.11, gain: 0.55, type: "sine", hold: 0.03 });
      });
      const start = 0.50, dur = 1.70, hold = dur * 0.75;
      tones.push({ freq: 261.63, t: start, dur, gain: 0.38, type: "sine", hold });
      tones.push({ freq: 329.63, t: start, dur, gain: 0.32, type: "sine", hold });
      tones.push({ freq: 392.00, t: start, dur, gain: 0.32, type: "sine", hold });
      return tones;
    },
    bar: () => {
      // Session 27.91 — Same rewrite applied one octave up.
      // Simpler triad, all sines, gains scaled to stay in the
      // compressor's linear region.
      const tones = [];
      [0.00, 0.14, 0.28].forEach((t) => {
        tones.push({ freq: 523.25, t, dur: 0.11, gain: 0.55, type: "sine", hold: 0.03 });
      });
      const start = 0.50, dur = 1.70, hold = dur * 0.75;
      tones.push({ freq: 523.25, t: start, dur, gain: 0.36, type: "sine", hold });
      tones.push({ freq: 659.25, t: start, dur, gain: 0.30, type: "sine", hold });
      tones.push({ freq: 783.99, t: start, dur, gain: 0.30, type: "sine", hold });
      return tones;
    },
  },
  {
    id: "bright_bell",
    label: "Bright Bell",
    description: "Five crisp, evenly-spaced dings — like a service bell at a hotel front desk. Zero sustain, all clarity.",
    kitchen: () => {
      // 5 dings at C5 with a G4 body underneath, spaced 0.42s apart.
      const tones = [];
      for (let i = 0; i < 5; i++) {
        const t = i * 0.42;
        tones.push({ freq: 523.25, t, dur: 0.32, gain: 0.90, type: "sine",     hold: 0.05 });
        tones.push({ freq: 392.00, t, dur: 0.32, gain: 0.55, type: "triangle", hold: 0.05 });
      }
      return tones;
    },
    bar: () => {
      // Same rhythm one octave higher, with a glass-clink overtone.
      const tones = [];
      for (let i = 0; i < 5; i++) {
        const t = i * 0.42;
        tones.push({ freq: 1046.50, t, dur: 0.32, gain: 0.85, type: "triangle", hold: 0.05 });
        tones.push({ freq: 2093.00, t, dur: 0.32, gain: 0.35, type: "sine",     hold: 0.05 });
      }
      return tones;
    },
  },
  {
    id: "mellow_marimba",
    label: "Mellow Marimba",
    description: "Soft descending marimba pattern. Calm without being quiet. Best for smaller venues that don't want a bell.",
    kitchen: () => {
      // Descending A4 → F4 → D4 marimba-like triangles, twice.
      const notes = [
        { freq: 440.00, dur: 0.42 }, // A4
        { freq: 349.23, dur: 0.42 }, // F4
        { freq: 293.66, dur: 0.55 }, // D4
      ];
      const tones = [];
      [0, 1.10].forEach((cycleStart) => {
        notes.forEach((n, i) => {
          const t = cycleStart + i * 0.30;
          // Triangle body + sine sub for warmth
          tones.push({ freq: n.freq,        t, dur: n.dur, gain: 0.90, type: "triangle", hold: n.dur * 0.3 });
          tones.push({ freq: n.freq / 2,    t, dur: n.dur, gain: 0.40, type: "sine",     hold: n.dur * 0.3 });
        });
      });
      return tones;
    },
    bar: () => {
      // Same descending shape one octave up (A5 → F5 → D5) with a
      // sparkle harmonic on top.
      const notes = [
        { freq: 880.00, dur: 0.42 },
        { freq: 698.46, dur: 0.42 },
        { freq: 587.33, dur: 0.55 },
      ];
      const tones = [];
      [0, 1.10].forEach((cycleStart) => {
        notes.forEach((n, i) => {
          const t = cycleStart + i * 0.30;
          tones.push({ freq: n.freq,      t, dur: n.dur, gain: 0.85, type: "triangle", hold: n.dur * 0.3 });
          tones.push({ freq: n.freq * 2,  t, dur: n.dur, gain: 0.25, type: "sine",     hold: n.dur * 0.3 });
        });
      });
      return tones;
    },
  },
  {
    id: "digital_chirp",
    label: "Digital Chirp",
    description: "Modern synth chirp with a rising major arpeggio. Short, clean, distinctly non-alarm-like.",
    kitchen: () => {
      // Rising C major arpeggio (C4-E4-G4-C5), sawtooth for a synth
      // edge, played twice with a small gap.
      const arp = [261.63, 329.63, 392.00, 523.25];
      const tones = [];
      [0, 1.05].forEach((base) => {
        arp.forEach((freq, i) => {
          const t = base + i * 0.14;
          tones.push({ freq, t, dur: 0.18, gain: 0.85, type: "sawtooth", hold: 0.05 });
          tones.push({ freq, t, dur: 0.18, gain: 0.35, type: "sine",     hold: 0.05 });
        });
      });
      return tones;
    },
    bar: () => {
      // Same arpeggio one octave up.
      const arp = [523.25, 659.25, 783.99, 1046.50];
      const tones = [];
      [0, 1.05].forEach((base) => {
        arp.forEach((freq, i) => {
          const t = base + i * 0.14;
          tones.push({ freq, t, dur: 0.18, gain: 0.80, type: "sawtooth", hold: 0.05 });
          tones.push({ freq: freq * 2, t, dur: 0.18, gain: 0.25, type: "sine", hold: 0.05 });
        });
      });
      return tones;
    },
  },
];

const _byId = Object.fromEntries(PRESETS.map((p) => [p.id, p]));
const _listeners = new Set();
let _cached = null;

function _readFromStorage() {
  try {
    if (typeof localStorage === "undefined") return DEFAULT_PRESET_ID;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw || !_byId[raw]) return DEFAULT_PRESET_ID;
    return raw;
  } catch (_) {
    return DEFAULT_PRESET_ID;
  }
}

/** List every available preset (for a picker UI). Read-only. */
export function listChimePresets() {
  return PRESETS.map((p) => ({ id: p.id, label: p.label, description: p.description }));
}

/** Get the current preset ID (defaults to warm_chord). */
export function getChimePresetId() {
  if (_cached == null) _cached = _readFromStorage();
  return _cached;
}

/** Set the current preset ID and notify subscribers. Rejects unknown IDs. */
export function setChimePresetId(id) {
  const next = _byId[id] ? id : DEFAULT_PRESET_ID;
  _cached = next;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, next);
    }
  } catch (_) { /* localStorage may be unavailable */ }
  for (const fn of _listeners) {
    try { fn(next); } catch (_) { /* noop */ }
  }
  return next;
}

/** Return the tone-map for the currently-selected preset + role. */
export function getPresetTones(role) {
  const preset = _byId[getChimePresetId()] || _byId[DEFAULT_PRESET_ID];
  if (role === "bar") return preset.bar();
  return preset.kitchen();
}

/** Return the tone-map for a specific preset id. Uses the kitchen variant
 *  (full-bodied, lower register) which sounds best on tablets/phones. */
export function getPresetTonesById(presetId) {
  const preset = _byId[presetId] || _byId[DEFAULT_PRESET_ID];
  return preset.kitchen();
}

/** Subscribe to preset changes (used by the picker so multiple open
 * popovers stay in sync). Returns an unsubscribe function. */
export function subscribeChimePreset(handler) {
  if (typeof handler !== "function") return () => {};
  _listeners.add(handler);
  try { handler(getChimePresetId()); } catch (_) { /* noop */ }
  return () => { _listeners.delete(handler); };
}

// ─────────────────────────────────────────────────────────────
// Session 27.72 — Per-event preset selection (Staff dashboard)
// ─────────────────────────────────────────────────────────────
// The Staff dashboard has three distinct sound events during a shift
// and managers wanted to pick a different chime for each so the ear
// can distinguish them instantly without looking at the tablet.
//
// Each event stores its own preset id in localStorage. The tone-map
// returned by `getStaffEventTones(event)` starts from the chosen
// preset's `.kitchen()` variant and is trimmed / softened per event
// so the three keep their distinct characters (new-order LOUD & long,
// order-ready SHORT & bright, confirmation SHORT & soft) even when
// the same preset is picked for all three.
const _eventCached = { new_order: null, order_ready: null, confirmation: null, long_wait: null };
const _eventListeners = { new_order: new Set(), order_ready: new Set(), confirmation: new Set(), long_wait: new Set() };

function _readEventFromStorage(event) {
  try {
    if (typeof localStorage === "undefined") return STAFF_EVENT_DEFAULTS[event];
    const raw = localStorage.getItem(STAFF_EVENT_STORAGE_KEYS[event]);
    if (!raw || !_byId[raw]) return STAFF_EVENT_DEFAULTS[event];
    return raw;
  } catch (_) {
    return STAFF_EVENT_DEFAULTS[event];
  }
}

/** List every Staff sound event with its label (for the picker UI). */
export function listStaffChimeEvents() {
  return Object.keys(STAFF_EVENT_LABELS).map((event) => ({
    event,
    label: STAFF_EVENT_LABELS[event],
  }));
}

/** Get the current preset id for a Staff event. Defaults per event.  */
export function getStaffEventPresetId(event) {
  if (!STAFF_EVENT_STORAGE_KEYS[event]) return DEFAULT_PRESET_ID;
  if (_eventCached[event] == null) _eventCached[event] = _readEventFromStorage(event);
  return _eventCached[event];
}

/** Set the preset id for a Staff event. Rejects unknown ids/events. */
export function setStaffEventPresetId(event, id) {
  if (!STAFF_EVENT_STORAGE_KEYS[event]) return null;
  const next = _byId[id] ? id : STAFF_EVENT_DEFAULTS[event];
  _eventCached[event] = next;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STAFF_EVENT_STORAGE_KEYS[event], next);
    }
  } catch (_) { /* noop */ }
  for (const fn of _eventListeners[event]) {
    try { fn(next); } catch (_) { /* noop */ }
  }
  return next;
}

/** Subscribe to preset changes for a specific Staff event. */
export function subscribeStaffEventPreset(event, handler) {
  if (typeof handler !== "function" || !_eventListeners[event]) return () => {};
  _eventListeners[event].add(handler);
  try { handler(getStaffEventPresetId(event)); } catch (_) { /* noop */ }
  return () => { _eventListeners[event].delete(handler); };
}

/**
 * Return the tone-map for a Staff event using the currently-selected
 * preset. All events now use the same full kitchen tone-map so the
 * sound is clean and consistent across all dashboards (Runner, Staff,
 * Kitchen, Bar). Only the confirmation event is slightly softened.
 */
export function getStaffEventTones(event) {
  const presetId = getStaffEventPresetId(event);
  const preset = _byId[presetId] || _byId[STAFF_EVENT_DEFAULTS[event]] || _byId[DEFAULT_PRESET_ID];
  if (event === "confirmation") {
    // Confirmation is subtle — halve the gain so the pop-up ping
    // isn't as loud as a real order notification.
    const tones = preset.kitchen();
    return tones.map((t) => ({ ...t, gain: (t.gain ?? 0.7) * 0.5 }));
  }
  if (event === "long_wait") {
    // Session 30.19 — Long-wait alert is a SOFT nudge, not an alarm:
    // ~60% gain and slightly slower spacing so it reads as "gentle
    // reminder" next to the punchy new-order chime.
    const tones = preset.kitchen();
    return tones.map((t) => ({ ...t, gain: (t.gain ?? 0.7) * 0.6, t: (t.t || 0) * 1.25 }));
  }
  // new_order, order_ready, and any other event — full kitchen
  // tone-map, unmodified, matching the Runner dashboard sound.
  return preset.kitchen();
}
